import { type ComponentType, type ReactNode } from "react";
import {
  SecondaryNavigation,
  SecondaryNavigationTitle,
  Tab,
  Tabs,
  Wrapper,
  type WrapperProps,
} from "@wfp/ui";

import Breadcrumb from "../Breadcrumb";

type PageStandardBackground = WrapperProps["background"] | string | null;

export interface PageStandardTab {
  id: string;
  label: ReactNode;
  Component: ComponentType<Record<string, unknown>>;
  props?: Record<string, unknown>;
  hide?: boolean;
}

interface PageStandardWithTabsProps {
  additional?: ReactNode;
  background?: PageStandardBackground;
  tabs: PageStandardTab[];
  title: ReactNode;
}

// @wfp/ui's Tab.d.ts marks href/role/selected/tabIndex as required, but Tabs
// injects them itself via cloneElement — the ambient types don't match the
// documented usage, so match the runtime contract instead.
const TabComponent = Tab as unknown as ComponentType<{ label: ReactNode; children: ReactNode }>;

const getWrapperBackground = (background: PageStandardBackground) =>
  background === null ? undefined : (background as WrapperProps["background"] | undefined);

const PageStandardWithTabs = ({
  additional,
  background = "lighter",
  tabs,
  title,
}: PageStandardWithTabsProps) => (
  <div>
    <SecondaryNavigation className="border-b-0" pageWidth="full" additional={additional}>
      <Breadcrumb />
      <SecondaryNavigationTitle>{title}</SecondaryNavigationTitle>
    </SecondaryNavigation>
    <Tabs className="ml-4">
      {tabs.map(({ id, label, Component, props, hide }) => {
        if (hide) {
          return null;
        }
        return (
          <TabComponent key={id} label={label}>
            <Wrapper pageWidth="full" spacing="md" background={getWrapperBackground(background)}>
              <Component {...props} />
            </Wrapper>
          </TabComponent>
        );
      })}
    </Tabs>
  </div>
);

export default PageStandardWithTabs;
